import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { ok } from '../../lib/api';
import { characterName, useRunScope } from '../../lib/characters';
import { QuickActionDialog } from './QuickActionDialog';
import { QUICK_ACTIONS, type QuickActionId } from './quickActions';

/**
 * The Quick actions bar at the top of the player detail (card #250): Rest up (was Patch up; ADR 0073), Supply drop, Homebase reset, Base
 * resupply, each opening a confirm dialog that says what will happen and shows the result. `extraActions` lets other
 * buttons (the Reset progress dialog) sit in the same bar. The state the dialogs describe comes from
 * `GET /admin/players/{id}/quick-actions`.
 */
export function QuickActionsBar({
  playerId,
  extraActions,
}: {
  playerId: string;
  extraActions?: ReactNode;
}) {
  const { client, runId, character } = useRunScope();
  const [open, setOpen] = useState<QuickActionId | null>(null);
  const context = useQuery({
    queryKey: ['admin-quick-actions', playerId, runId],
    queryFn: async () =>
      ok(
        await client.GET('/admin/players/{id}/quick-actions', {
          params: { path: { id: playerId } },
        }),
      ),
  });

  return (
    <section aria-label="Quick actions" className="mb-4 rounded border-2 border-ink bg-white p-3">
      <div className="flex flex-wrap items-stretch gap-2">
        <h2 className="mr-2 self-center font-semibold">Quick actions</h2>
        {QUICK_ACTIONS.map((a) => (
          <button
            key={a.id}
            type="button"
            className="rounded border-2 border-ink px-3 py-1 text-left hover:bg-caption/40 disabled:opacity-40"
            disabled={!context.data}
            onClick={() => setOpen(a.id)}
          >
            <span className="block text-sm font-semibold">{a.label}</span>
            <span className="block text-xs text-bruise">{a.subtitle}</span>
          </button>
        ))}
        {extraActions}
      </div>
      {context.isError && <p className="mt-2 text-sm text-blam">{String(context.error)}</p>}
      {(context.data?.runStatus === 'dead' || context.data?.runStatus === 'zombie') && (
        <p className="mt-2 text-sm">
          {character ? characterName(character) : 'The character'} is a zombie (run{' '}
          {context.data.runNumber}).{' '}
          {context.data.canRevive
            ? 'Rest up resurrects them in place.'
            : (context.data.reviveBlocker ?? '')}
        </p>
      )}
      {open && context.data && (
        <QuickActionDialog
          playerId={playerId}
          action={open}
          context={context.data}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}
